<template>
  <div class="social-proof-dialog">
    <v-form ref="form" v-model="isFormValid">
      <v-row>
        <v-col cols="12">
          <VaTinymceInput
            title="Review text"
            required
            :display-errors="displayErrors"
            :text-only-with-line-height="true"
            :editor-height="220"
            :current-content="formData.reviewText"
            @update="val => (formData.reviewText = val)" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div class="text-caption grey--text text--darken-1 mb-1">Layout</div>
          <v-btn-toggle v-model="formData.layout" mandatory dense>
            <v-btn value="stacked" small title="Stacked">
              <v-icon small>mdi-arrow-down</v-icon>
            </v-btn>
            <v-btn value="side-by-side" small title="Side by side">
              <v-icon small>mdi-arrow-right</v-icon>
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-select v-model="formData.alignment" :items="alignmentOptions" label="Alignment" persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model.number="formData.imageSize"
            label="Photo size (px)"
            type="number"
            min="1"
            max="300"
            :rules="[v => (v >= 1 && v <= 300) || 'Photo size must be between 1-300px']"
            suffix="px"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-text-field
            v-model.number="formData.borderRadius"
            label="Corner radius (px)"
            type="number"
            min="0"
            max="50"
            :rules="[v => (v >= 0 && v <= 50) || 'Corner radius must be between 0-50px']"
            suffix="px"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox v-model="formData.enableMaxWidth" label="Custom max width" hide-details class="mt-0 mb-2" />
          <v-text-field
            v-if="formData.enableMaxWidth"
            v-model.number="formData.maxWidth"
            label="Max width (px)"
            type="number"
            min="200"
            :rules="[v => v >= 200 || 'Max width must be at least 200px']"
            suffix="px"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <VaMediaObjectFileInput label="Photo" :item-id="formData.imageID" @update:photo="updatePhoto" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox v-model="formData.enableOutline" label="Photo outline" hide-details class="mt-0 mb-2" />
          <VaColorPicker
            v-if="formData.enableOutline"
            :position="'top'"
            :title="'Outline of the photo'"
            v-model="formData.imageOutline"></VaColorPicker>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-checkbox v-model="formData.enableBackground" label="Background color" hide-details class="mt-0 mb-2" />
          <VaColorPicker
            v-if="formData.enableBackground"
            :position="'top'"
            :title="'Background color'"
            v-model="formData.backgroundColor"></VaColorPicker>
        </v-col>
      </v-row>

      <v-alert v-if="errorMessage" type="error" class="mb-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  import cloneDeep from "lodash/cloneDeep";
  import { SOCIAL_PROOF_V2_EVENT_TYPES } from "@/constant";
  import VaColorPicker from "@/components/custom/VaColorPicker.vue";
  import VaTinymceInput from "@/components/custom/VaTinymceInput.vue";

  const DEFAULT_FORM_DATA = {
    reviewText: "",
    imageID: "",
    imageURL: "",
    enableOutline: false,
    imageOutline: "#37A9FC",
    enableBackground: false,
    backgroundColor: "#F0F9FF",
    alignment: "center",
    layout: "stacked",
    imageSize: 64,
    borderRadius: 12,
    enableMaxWidth: false,
    maxWidth: 800,
  };

  const isNonEmptyHtml = html => {
    if (!html) return false;
    return (
      html
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "")
        .trim().length > 0
    );
  };

  export default {
    name: "SocialProofV2Dialog",
    components: {
      VaColorPicker,
      VaTinymceInput,
    },
    data() {
      return {
        isFormValid: false,
        displayErrors: false,
        errorMessage: "",
        formData: cloneDeep(DEFAULT_FORM_DATA),
        alignmentOptions: [
          { text: "Left", value: "left" },
          { text: "Center", value: "center" },
          { text: "Right", value: "right" },
        ],
      };
    },
    computed: {
      isAllValid() {
        return this.isFormValid && isNonEmptyHtml(this.formData.reviewText);
      },
    },
    watch: {
      isAllValid: {
        handler() {
          this.sendValidation();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      setupReceiveDataListener() {
        window.onmessage = async message => {
          if (message.data.type === SOCIAL_PROOF_V2_EVENT_TYPES.NEED_VALIDATION) {
            this.displayErrors = true;
            await this.$refs.form.validate();
            this.errorMessage = this.isAllValid ? "" : "Please fill in the required fields";
          }
          if (message.data.type === SOCIAL_PROOF_V2_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data && Object.keys(data).length > 0) {
              this.formData = {
                ...DEFAULT_FORM_DATA,
                ...data,
                layout: data.layout === "side-by-side" ? "side-by-side" : "stacked",
              };
            } else {
              this.updateValue();
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: SOCIAL_PROOF_V2_EVENT_TYPES.REQUEST_DATA,
          },
          "*",
        );
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isAllValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: SOCIAL_PROOF_V2_EVENT_TYPES.UPDATE_SOCIAL_PROOF_DATA,
            data: cloneDeep(this.formData),
          },
          "*",
        );
      },
      sendValidation() {
        window.parent.postMessage(
          {
            mceAction: SOCIAL_PROOF_V2_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isAllValid,
            },
          },
          "*",
        );
      },
      updatePhoto(photoData) {
        if (photoData && photoData !== "") {
          this.formData.imageID = photoData["@id"];
          this.formData.imageURL = photoData.contentUrl;
        } else {
          this.formData.imageID = "";
          this.formData.imageURL = "";
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .social-proof-dialog {
    padding: 20px;

    .row {
      margin-top: 0;
      margin-bottom: 0;
    }

    .col {
      padding-top: 4px;
      padding-bottom: 4px;
    }

    ::v-deep .v-text-field__suffix {
      padding-right: 6px;
    }
  }
</style>
